/**
 * Sub-goal design-store catalog. Twin of `followerGoalDesignsCatalog`: a static
 * gallery of self-contained card SVGs the user can apply to the SubGoal widget.
 * Recreated 1:1 from the Claude Design `Sub Goal Widgets.html` handoff (10
 * variants), cropped from the original 1920×1080 scenes (the `translate(120 760)`
 * card group → a tight `0 0 700 260` card) and re-parametrised so the live sub
 * count drives BOTH the readout text (`current/target · pct%`) AND the fill
 * geometry (bar width + the moving head: dark/liquid knob · pixel star ·
 * cottage flower · anime heart).
 *
 * The designs are now PURE DATA ({@link SerializableDesign}): each carries a
 * `frame` SVG with `{{name}}` placeholders, `slots` for the readout numbers,
 * `computed` formulas for the %-driven geometry and `fragments` for the
 * conditional fill / moving-head markup spliced at the `{{FRAGMENTS:…}}` markers.
 * `renderSubGoalDesignSvg` is the single entry point the Store thumbnail, the
 * config picker/preview and the live Pixi renderer all call; it maps the counts
 * to {@link renderDesign}. No per-design `build()` JS is transported or run, so
 * a published design can be sanitised and is safe.
 *
 * Card-SVG conventions (shared with the other catalogs):
 *  - viewBox is `0 0 cardWidth cardHeight`; NO width/height attrs (the raster
 *    injects them).
 *  - every `<defs>`/gradient/filter/pattern/`<g id>` id is prefixed with the
 *    design id (e.g. `dark-sgd-bar`) so 10 cards inline in one DOM document
 *    without id collisions; every `url(#…)`/`href="#…"` ref matches.
 *  - the only dynamic substitutions are NUMBERS (current/target/pct) and derived
 *    geometry, so no XML-escaping is needed.
 *
 * Known caveat: the Cyberpunk (`サブ`), Anime VHS (`サブ`/`ありがとう`) and assorted
 * emoji (★ 🌱 💧 ♥) keep the handoff's accents, but the display fonts (Orbitron /
 * VT323 / Audiowide …) carry no CJK/emoji glyphs, so those fall back to a system
 * face in the raster — intentional, matches the mock. Some purely decorative
 * overlays (y2k gloss, cyber notch ticks, tier stars, cottage vine) stay fixed.
 */

import { renderDesign } from "../entities/designs/renderDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";
import type { SerializableDesign } from "../entities/designs/serializableDesign";

export type SubGoalDesignCategory = "sub";

/** The numbers shown on the card. `pct` is already clamped to [0,100]. */
export interface SubGoalParts {
  /** Raw current count (may exceed `target`; the readout shows it verbatim). */
  current: number;
  target: number;
  /** `round(current/target*100)` clamped to [0,100] — drives fill + readout. */
  pct: number;
}

/**
 * A SubGoal design is a {@link SerializableDesign} pinned to `widgetType:
 * "subgoal"` and the local `"sub"` category, keeping the catalog's metadata
 * fields (swatches/fonts/…) for the Store gallery.
 */
export type SubGoalDesign = SerializableDesign & {
  widgetType: "subgoal";
  category: SubGoalDesignCategory;
};

const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n));

/**
 * Emit one pixel-meter full block (`<rect x>` fixed, width 28) for slot index
 * `i`: shown only when the fill width `fw` reaches its right edge
 * (`34*i + 28 <= fw`). Mirrors the old `pixelBlocks` loop's full-block stride
 * (start x=6, stride 34, block width 28). 18 slots cover the 600px track.
 */
const pixelFullBlock = (i: number) => ({
  key: "blocks",
  showIf: `${34 * i + 28} <= fw`,
  svg: `<rect x="${6 + 34 * i}" y="6" width="28" height="28"/>`,
});

// ── 01 · Dark Minimal ──────────────────────────────────────────────────────
const darkDesign: SubGoalDesign = {
  id: "dark",
  name: "Dark Minimal",
  subtitle: "Reference style",
  widgetType: "subgoal",
  category: "sub",
  description:
    '"SUB GOAL" left, "current/target · pct%" right, soft violet rounded bar with glow knob and a caption below.',
  swatches: ["#0d0d12", "#1f1f29", "#a07dff", "#f5f5f5"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Inter", "DM Mono"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
    pct: { kind: "number" },
  },
  computed: {
    fw: "round(604 * pct / 100)",
    knob: "48 + round(604 * pct / 100)",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="dark-sgd-dots" width="22" height="22" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1.1" fill="#33333d" opacity=".6"/></pattern>
    <linearGradient id="dark-sgd-bar" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#7d5cff"/><stop offset="100%" stop-color="#a07dff"/></linearGradient>
    <filter id="dark-sgd-glow"><feGaussianBlur stdDeviation="6"/></filter>
  </defs>
  <rect width="700" height="260" rx="22" fill="#0d0d12" stroke="#2a2a33" stroke-width="1.5"/>
  <rect width="700" height="260" rx="22" fill="url(#dark-sgd-dots)"/>
  <text x="48" y="92" font-family="Inter, sans-serif" font-weight="700" font-size="28" fill="#f5f5f5" letter-spacing="3">SUB GOAL</text>
  <text x="652" y="92" text-anchor="end" font-family="DM Mono, monospace" font-weight="500" font-size="26" fill="#cfd6e2">{{current}}/{{target}} · {{pct}}%</text>
  <rect x="48" y="120" width="604" height="34" rx="17" fill="#1f1f29"/>
  {{FRAGMENTS}}
  <text x="48" y="208" font-family="Inter, sans-serif" font-weight="500" font-size="20" fill="#8a8f9c">¡suscribite y ayudanos a la meta! ★</text>
</svg>`,
  fragments: [
    {
      showIf: "fw > 0",
      svg: `<rect x="48" y="120" width="{{fw}}" height="34" rx="17" fill="url(#dark-sgd-bar)"/>`,
    },
    {
      showIf: "fw > 0",
      svg: `<circle cx="{{knob}}" cy="137" r="22" fill="#a07dff" filter="url(#dark-sgd-glow)" opacity=".55"/><circle cx="{{knob}}" cy="137" r="12" fill="#ece4ff"/>`,
    },
  ],
};

// ── 02 · Tier Pips ─────────────────────────────────────────────────────────
const tiersDesign: SubGoalDesign = {
  id: "tiers",
  name: "Tier Pips",
  subtitle: "Star milestones",
  widgetType: "subgoal",
  category: "sub",
  description:
    "A pip / star track with the goal filled in violet, lit stars at tier milestones and the live count.",
  swatches: ["#101014", "#9146ff", "#ffd24a", "#bf94ff"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Space Grotesk", "DM Mono", "Inter"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
  },
  computed: {
    fw: "round(620 * pct / 100)",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="tiers-sgt-glow"><feGaussianBlur stdDeviation="4"/></filter></defs>
  <rect width="700" height="260" rx="18" fill="#101014" stroke="#26262e" stroke-width="1.5"/>
  <text x="40" y="62" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="26" fill="#fff" letter-spacing="1">META DE SUBS</text>
  <text x="660" y="62" text-anchor="end" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="26" fill="#ffd24a">{{current}} / {{target}}</text>
  <g transform="translate(40 110)">
    <rect width="620" height="30" rx="15" fill="#1c1c26"/>
    {{FRAGMENTS}}
    <g fill="#ffd24a"><polygon points="155,-6 161,8 176,8 164,18 168,33 155,24 142,33 146,18 134,8 149,8"/></g>
    <g fill="#ffd24a" filter="url(#tiers-sgt-glow)" opacity=".7"><polygon points="310,-6 316,8 331,8 319,18 323,33 310,24 297,33 301,18 289,8 304,8"/></g>
    <g fill="#3a3a46"><polygon points="465,-6 471,8 486,8 474,18 478,33 465,24 452,33 456,18 444,8 459,8"/></g>
  </g>
  <g font-family="DM Mono, monospace" font-size="14" fill="#6b6e7a" letter-spacing="2"><text x="40" y="186">TIER 1</text><text x="350" y="186" text-anchor="middle" fill="#ffd24a">TIER 2</text><text x="660" y="186" text-anchor="end">TIER 3</text></g>
  <text x="40" y="226" font-family="Inter, sans-serif" font-weight="500" font-size="18" fill="#8a8f9c">¡sumate al próximo perk ✦</text>
</svg>`,
  fragments: [
    {
      showIf: "fw > 0",
      svg: `<rect width="{{fw}}" height="30" rx="15" fill="#9146ff"/>`,
    },
  ],
};

// ── 03 · Y2K Aqua Gel ──────────────────────────────────────────────────────
const y2kDesign: SubGoalDesign = {
  id: "y2k",
  name: "Y2K Aqua Gel",
  subtitle: "Glossy",
  widgetType: "subgoal",
  category: "sub",
  description:
    "Aqua gel capsule with chrome gloss, green liquid fill and the sub readout in Audiowide.",
  swatches: ["#003a7a", "#1c8fff", "#8acfff", "#5aff8a"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Audiowide", "DM Mono"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
    pct: { kind: "number" },
  },
  computed: {
    fw: "round(594 * pct / 100)",
    glossCx: "54 + round(594 * pct / 100) / 2",
    glossRx: "max(0, round(594 * pct / 100) / 2 - 12)",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="y2k-sgy-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeaff"/><stop offset="55%" stop-color="#1c8fff"/><stop offset="100%" stop-color="#003a7a"/></linearGradient>
    <linearGradient id="y2k-sgy-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#caffd6"/><stop offset="45%" stop-color="#5aff8a"/><stop offset="100%" stop-color="#2cd86a"/></linearGradient>
  </defs>
  <rect width="700" height="260" rx="40" fill="url(#y2k-sgy-bg)" stroke="#fff" stroke-width="3"/>
  <ellipse cx="350" cy="30" rx="300" ry="12" fill="#fff" opacity=".7"/>
  <g transform="translate(40 40)"><circle r="10" fill="#003a7a"/><circle r="6" fill="#5aff8a"/></g>
  <text x="62" y="48" font-family="Audiowide, sans-serif" font-size="26" fill="#fff" letter-spacing="2">SUB GOAL</text>
  <text x="652" y="48" text-anchor="end" font-family="Audiowide, sans-serif" font-size="24" fill="#5aff8a">{{current}}/{{target}}·{{pct}}%</text>
  <rect x="48" y="110" width="604" height="50" rx="25" fill="rgba(0,30,80,.5)" stroke="#fff" stroke-width="2"/>
  {{FRAGMENTS}}
  <text x="48" y="216" font-family="DM Mono, monospace" font-size="15" fill="#cffff4" letter-spacing="6">¡GRACIAS POR SUSCRIBIRTE! ♥</text>
</svg>`,
  fragments: [
    {
      showIf: "fw > 0",
      svg: `<rect x="54" y="116" width="{{fw}}" height="38" rx="19" fill="url(#y2k-sgy-fill)"/>`,
    },
    {
      showIf: "fw > 0",
      svg: `<ellipse cx="{{glossCx}}" cy="128" rx="{{glossRx}}" ry="6" fill="#fff" opacity=".6"/>`,
    },
  ],
};

// ── 04 · Cyberpunk Neon ────────────────────────────────────────────────────
const cyberDesign: SubGoalDesign = {
  id: "cyber",
  name: "Cyberpunk Neon",
  subtitle: "HUD node",
  widgetType: "subgoal",
  category: "sub",
  description:
    'Chamfered HUD with "SUB.NODE" tag, notched neon fill, milestone ticks and katakana accent.',
  swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["DM Mono", "Orbitron"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
    pct: { kind: "number" },
  },
  computed: {
    fw: "round(624 * pct / 100)",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="cyber-sgc-g"><feGaussianBlur stdDeviation="2.5"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
  <g filter="url(#cyber-sgc-g)">
    <polygon points="0,40 40,0 660,0 700,40 700,220 660,260 40,260 0,220" fill="#080010" stroke="#ff2e88" stroke-width="2.5"/>
    <polygon points="14,46 50,14 650,14 686,46 686,214 650,246 50,246 14,214" fill="none" stroke="#00f0ff" stroke-width="1.5" opacity=".7"/>
    <text x="34" y="64" font-family="DM Mono, monospace" font-size="20" fill="#00f0ff" letter-spacing="6">▮ SUB.NODE · サブ</text>
    <text x="666" y="64" text-anchor="end" font-family="Orbitron, sans-serif" font-weight="900" font-size="26" fill="#f5d019">{{current}}/{{target}}·{{pct}}%</text>
    <g transform="translate(34 110)">
      <rect width="632" height="40" fill="none" stroke="#00f0ff" stroke-width="1.5"/>
      {{FRAGMENTS}}
      <g fill="#080010"><rect x="64" y="4" width="3" height="32"/><rect x="128" y="4" width="3" height="32"/><rect x="192" y="4" width="3" height="32"/><rect x="256" y="4" width="3" height="32"/><rect x="320" y="4" width="3" height="32"/><rect x="384" y="4" width="3" height="32"/></g>
      <g stroke="#ff2e88" stroke-width="2"><line x1="316" y1="-8" x2="316" y2="48"/></g>
    </g>
    <text x="34" y="216" font-family="DM Mono, monospace" font-size="14" fill="#88e8d8" letter-spacing="6">// SUSCRIBITE · NEXT PERK</text>
  </g>
</svg>`,
  fragments: [
    {
      showIf: "fw > 0",
      svg: `<rect x="4" y="4" width="{{fw}}" height="32" fill="#00f0ff"/>`,
    },
  ],
};

// ── 05 · Editorial ─────────────────────────────────────────────────────────
const editorialDesign: SubGoalDesign = {
  id: "editorial",
  name: "Editorial",
  subtitle: "Subscriber roll",
  widgetType: "subgoal",
  category: "sub",
  description:
    'Cream card, double rule, italic "Meta de suscriptores" and a thin ochre fill with serif readout.',
  swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Cormorant Garamond", "DM Mono"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
    pct: { kind: "number" },
  },
  computed: {
    fw: "round(632 * pct / 100)",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <rect width="700" height="260" fill="#f5efe2"/>
  <line x1="24" y1="22" x2="676" y2="22" stroke="#1a1815" stroke-width="3"/>
  <line x1="24" y1="30" x2="676" y2="30" stroke="#1a1815" stroke-width="1"/>
  <text x="32" y="86" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="44" fill="#1a1815">Meta de suscriptores</text>
  <text x="668" y="86" text-anchor="end" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="38" fill="#8a3a2a">{{current}} / {{target}}</text>
  <rect x="32" y="116" width="636" height="22" fill="none" stroke="#1a1815" stroke-width="1.5"/>
  {{FRAGMENTS}}
  <line x1="32" y1="166" x2="668" y2="166" stroke="#1a1815" stroke-width="1"/>
  <text x="32" y="204" font-family="DM Mono, monospace" font-size="14" fill="#1a1815" letter-spacing="8">{{pct}}% · ¡GRACIAS POR SUSCRIBIRTE!</text>
</svg>`,
  fragments: [
    {
      showIf: "fw > 0",
      svg: `<rect x="34" y="118" width="{{fw}}" height="18" fill="#c8a050"/>`,
    },
  ],
};

// ── 06 · Pixel Arcade ──────────────────────────────────────────────────────
const pixelDesign: SubGoalDesign = {
  id: "pixel",
  name: "Pixel Arcade",
  subtitle: "Power meter",
  widgetType: "subgoal",
  category: "sub",
  description:
    '8-bit dialog box with a blocky power fill, star sprite and "SUB GOAL" in Press Start 2P.',
  swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Press Start 2P"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
    pct: { kind: "number" },
  },
  computed: {
    fw: "round(600 * pct / 100)",
    starX: "50 + round(600 * pct / 100)",
    // full-block count: floor((fw-28)/34)+1 (>=0 for fw<28); the partial block
    // sits at its right edge (rem = fw - 34*full), shown only when rem > 4.
    pxFull: "max(0, floor((round(600 * pct / 100) - 28) / 34) + 1)",
    pxPartX: "6 + 34 * max(0, floor((round(600 * pct / 100) - 28) / 34) + 1)",
    pxPartRem:
      "round(600 * pct / 100) - 34 * max(0, floor((round(600 * pct / 100) - 28) / 34) + 1)",
    pxPartW:
      "min(28, round(round(600 * pct / 100) - 34 * max(0, floor((round(600 * pct / 100) - 28) / 34) + 1)))",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
  <rect width="700" height="260" fill="#48cae4"/>
  <rect x="8" y="8" width="684" height="244" fill="#1d2a3a"/>
  <rect x="16" y="16" width="668" height="228" fill="#0a121c"/>
  <g fill="#ffb703"><rect width="24" height="24"/><rect x="676" width="24" height="24"/><rect y="236" width="24" height="24"/><rect x="676" y="236" width="24" height="24"/></g>
  <g transform="translate(40 -16)"><rect width="240" height="32" fill="#ff3d4d"/><rect x="6" y="6" width="228" height="20" fill="#1d2a3a"/><text x="120" y="22" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="12" fill="#ffb703" letter-spacing="1">SUB GOAL</text></g>
  <g font-family="Press Start 2P, monospace" fill="#fff"><text x="44" y="98" font-size="16" fill="#48cae4">{{current}} / {{target}}</text><text x="656" y="98" text-anchor="end" font-size="16" fill="#ffb703">{{pct}}%</text></g>
  <g transform="translate(44 120)"><rect width="612" height="40" fill="#000" stroke="#48cae4" stroke-width="3"/><g fill="#ff3d4d">{{FRAGMENTS:blocks}}</g></g>
  {{FRAGMENTS:star}}
  <text x="44" y="208" font-family="Press Start 2P, monospace" font-size="11" fill="#48cae4">SUB TO POWER UP!</text>
</svg>`,
  fragments: [
    pixelFullBlock(0),
    pixelFullBlock(1),
    pixelFullBlock(2),
    pixelFullBlock(3),
    pixelFullBlock(4),
    pixelFullBlock(5),
    pixelFullBlock(6),
    pixelFullBlock(7),
    pixelFullBlock(8),
    pixelFullBlock(9),
    pixelFullBlock(10),
    pixelFullBlock(11),
    pixelFullBlock(12),
    pixelFullBlock(13),
    pixelFullBlock(14),
    pixelFullBlock(15),
    pixelFullBlock(16),
    {
      key: "blocks",
      showIf: "pxPartRem > 4",
      svg: `<rect x="{{pxPartX}}" y="6" width="{{pxPartW}}" height="28"/>`,
    },
    {
      key: "star",
      showIf: "fw > 0",
      svg: `<g transform="translate({{starX}} 140)" fill="#ffb703"><rect x="12" y="0" width="6" height="6"/><rect x="6" y="6" width="18" height="6"/><rect x="0" y="12" width="30" height="6"/><rect x="6" y="18" width="6" height="6"/><rect x="18" y="18" width="6" height="6"/></g>`,
    },
  ],
};

// ── 07 · Cottagecore ───────────────────────────────────────────────────────
const cottageDesign: SubGoalDesign = {
  id: "cottage",
  name: "Cottagecore",
  subtitle: "Stitched vine",
  widgetType: "subgoal",
  category: "sub",
  description:
    "Linen card with cross-stitch border, italic goal and a vine fill with a flower marker.",
  swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Cormorant Garamond", "DM Mono"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
  },
  computed: {
    fw: "round(596 * pct / 100)",
    flower: "52 + round(596 * pct / 100)",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="cottage-sgco-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="cottage-sgco-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
    <g id="cottage-sgco-flower"><circle r="12" fill="#ff9da8"/><circle cx="-12" cy="-5" r="9" fill="#ff9da8"/><circle cx="12" cy="-5" r="9" fill="#ff9da8"/><circle cx="-7" cy="9" r="9" fill="#ff9da8"/><circle cx="7" cy="9" r="9" fill="#ff9da8"/><circle r="5" fill="#ffd060"/></g>
  </defs>
  <rect width="700" height="260" fill="url(#cottage-sgco-linen)" stroke="#6b4a2a" stroke-width="2"/>
  <rect width="700" height="24" fill="url(#cottage-sgco-st)"/>
  <rect y="236" width="700" height="24" fill="url(#cottage-sgco-st)"/>
  <rect width="24" height="260" fill="url(#cottage-sgco-st)"/>
  <rect x="676" width="24" height="260" fill="url(#cottage-sgco-st)"/>
  <text x="48" y="92" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="40" fill="#2a1a08">Meta de suscriptores</text>
  <text x="652" y="92" text-anchor="end" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="36" fill="#5a7a4a">{{current}}/{{target}}</text>
  <rect x="48" y="120" width="604" height="26" rx="13" fill="#e8dcc0" stroke="#6b4a2a" stroke-width="1.5"/>
  {{FRAGMENTS:fill}}
  <path d="M52 133 q40 -10 80 0 q40 10 80 0 q40 -10 80 0 q40 10 80 0" stroke="#7a9a5a" stroke-width="2" fill="none"/>
  {{FRAGMENTS:flower}}
  <text x="48" y="210" font-family="DM Mono, monospace" font-size="14" fill="#b85a4a" letter-spacing="6">¡gracias por sumarte al jardín! 🌱</text>
</svg>`,
  fragments: [
    {
      key: "fill",
      showIf: "fw > 0",
      svg: `<rect x="52" y="124" width="{{fw}}" height="18" rx="9" fill="#5a7a4a"/>`,
    },
    {
      key: "flower",
      showIf: "fw > 0",
      svg: `<use href="#cottage-sgco-flower" transform="translate({{flower}} 133) scale(0.8)"/>`,
    },
  ],
};

// ── 08 · Brutalist ─────────────────────────────────────────────────────────
const brutalistDesign: SubGoalDesign = {
  id: "brutalist",
  name: "Brutalist",
  subtitle: "Ledger",
  widgetType: "subgoal",
  category: "sub",
  description:
    "Black slab with orange bar, huge Anton count and a hard fill with mono readout.",
  swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Anton", "DM Mono"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
    pct: { kind: "number" },
  },
  computed: {
    fw: "round(620 * pct / 100)",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <rect width="700" height="260" fill="#1a1a1a"/>
  <rect width="24" height="260" fill="#ff5a2a"/>
  <text x="48" y="58" font-family="DM Mono, monospace" font-size="14" fill="#ff5a2a" letter-spacing="12">// SUB GOAL</text>
  <text x="48" y="150" font-family="Anton, sans-serif" font-size="100" fill="#f5f5f5" letter-spacing="-2">{{current}}<tspan font-size="48" fill="#c8c2b8">/{{target}}</tspan></text>
  <text x="668" y="100" text-anchor="end" font-family="Anton, sans-serif" font-size="48" fill="#ff5a2a">{{pct}}%</text>
  <rect x="48" y="178" width="620" height="30" fill="#0a0a0a" stroke="#c8c2b8" stroke-width="1"/>
  {{FRAGMENTS}}
  <text x="48" y="240" font-family="DM Mono, monospace" font-size="13" fill="#c8c2b8" letter-spacing="8">SUSCRIBITE · @ERIOL.STREAMS</text>
</svg>`,
  fragments: [
    {
      showIf: "fw > 0",
      svg: `<rect x="48" y="178" width="{{fw}}" height="30" fill="#ff5a2a"/>`,
    },
  ],
};

// ── 09 · Anime VHS ─────────────────────────────────────────────────────────
const animeDesign: SubGoalDesign = {
  id: "anime",
  name: "Anime VHS",
  subtitle: "サブ",
  widgetType: "subgoal",
  category: "sub",
  description:
    "Pink double-stroke card with cyan tracking ghost, katakana corner and a heart-tipped RGB-split fill.",
  swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["DM Mono", "VT323"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
    pct: { kind: "number" },
  },
  computed: {
    fw: "round(626 * pct / 100)",
    heart: "36 + round(626 * pct / 100)",
    heartA: "36 + round(626 * pct / 100) - 10",
    heartB: "36 + round(626 * pct / 100) + 20",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="anime-sga-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="anime-sga-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".15"/></pattern>
    <linearGradient id="anime-sga-fill" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#ff7ad0"/><stop offset="100%" stop-color="#7ad0ff"/></linearGradient>
  </defs>
  <g filter="url(#anime-sga-rgb)">
    <rect x="6" y="0" width="700" height="260" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".55"/>
    <rect width="700" height="260" fill="#0a0218" stroke="#ff7ad0" stroke-width="4"/>
    <rect x="10" y="10" width="680" height="240" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".55"/>
    <rect width="700" height="260" fill="url(#anime-sga-scan)"/>
    <g><rect width="200" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="4">サブ · SUB</text></g>
    <text x="32" y="116" font-family="VT323, monospace" font-size="40" fill="#fff7c2" letter-spacing="4">SUB GOAL</text>
    <text x="666" y="116" text-anchor="end" font-family="VT323, monospace" font-size="40" fill="#ff7ad0">{{current}}/{{target}}·{{pct}}%</text>
    <rect x="32" y="138" width="636" height="36" fill="#1a0a2a" stroke="#7ad0ff" stroke-width="2"/>
    {{FRAGMENTS}}
    <text x="32" y="216" font-family="VT323, monospace" font-size="24" fill="#7ad0ff" letter-spacing="6">▮ ¡SUSCRIBITE! · ありがとう !</text>
  </g>
</svg>`,
  fragments: [
    {
      showIf: "fw > 0",
      svg: `<rect x="36" y="142" width="{{fw}}" height="28" fill="url(#anime-sga-fill)"/>`,
    },
    {
      showIf: "fw > 0",
      svg: `<path d="M{{heart}} 156 q-10 -10 0 -20 q10 -10 20 0 q10 -10 20 0 q10 10 0 20 l-20 18 z" fill="#fff7c2"/>`,
    },
  ],
};

// ── 10 · Liquid Glass ──────────────────────────────────────────────────────
const liquidDesign: SubGoalDesign = {
  id: "liquid",
  name: "Liquid Glass",
  subtitle: "Frosted",
  widgetType: "subgoal",
  category: "sub",
  description:
    "Frosted glass bar over a cobalt gradient with translucent fill, inner highlight strip and bokeh tip.",
  swatches: ["#0a1230", "#1c3a7a", "#9ad4ff", "#ffffff"],
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Space Grotesk", "DM Mono"],
  slots: {
    current: { kind: "number" },
    target: { kind: "number" },
    pct: { kind: "number" },
  },
  computed: {
    fw: "round(608 * pct / 100)",
    fwMinus12: "round(608 * pct / 100) - 12",
    tip: "46 + round(608 * pct / 100)",
  },
  frame: `<svg viewBox="0 0 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="liquid-sgl-bg" cx="40%" cy="30%" r="90%"><stop offset="0%" stop-color="#2a4a9a"/><stop offset="60%" stop-color="#0a1230"/><stop offset="100%" stop-color="#020615"/></radialGradient>
    <linearGradient id="liquid-sgl-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeeff"/><stop offset="50%" stop-color="#5aaaff"/><stop offset="100%" stop-color="#1c5aff"/></linearGradient>
    <radialGradient id="liquid-sgl-bokeh" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#9ad4ff" stop-opacity=".6"/><stop offset="100%" stop-color="#9ad4ff" stop-opacity="0"/></radialGradient>
    <filter id="liquid-sgl-blur"><feGaussianBlur stdDeviation="20"/></filter>
  </defs>
  <rect width="700" height="260" rx="28" fill="url(#liquid-sgl-bg)" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
  <g filter="url(#liquid-sgl-blur)" opacity=".7"><circle cx="120" cy="60" r="100" fill="url(#liquid-sgl-bokeh)"/><circle cx="600" cy="200" r="120" fill="url(#liquid-sgl-bokeh)"/></g>
  <text x="40" y="80" font-family="Space Grotesk, sans-serif" font-weight="600" font-size="26" fill="#fff" letter-spacing="2">META DE SUBS</text>
  <text x="660" y="80" text-anchor="end" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="32" fill="#9ad4ff">{{current}}/{{target}} · {{pct}}%</text>
  <rect x="40" y="110" width="620" height="44" rx="22" fill="rgba(255,255,255,.10)" stroke="rgba(255,255,255,.35)" stroke-width="1.5"/>
  {{FRAGMENTS}}
  <text x="40" y="200" font-family="DM Mono, monospace" font-size="14" fill="#9ad4ff" letter-spacing="6">¡GRACIAS POR SUSCRIBIRTE! 💧</text>
</svg>`,
  fragments: [
    {
      showIf: "fw > 0",
      svg: `<rect x="46" y="116" width="{{fw}}" height="32" rx="16" fill="url(#liquid-sgl-fill)"/>`,
    },
    {
      showIf: "fw > 12",
      svg: `<rect x="52" y="120" width="{{fwMinus12}}" height="10" rx="5" fill="#fff" opacity=".5"/>`,
    },
    {
      showIf: "fw > 0",
      svg: `<circle cx="{{tip}}" cy="132" r="22" fill="url(#liquid-sgl-bokeh)"/>`,
    },
  ],
};

export const SUB_GOAL_DESIGNS: SubGoalDesign[] = [
  darkDesign,
  tiersDesign,
  y2kDesign,
  cyberDesign,
  editorialDesign,
  pixelDesign,
  cottageDesign,
  brutalistDesign,
  animeDesign,
  liquidDesign,
];

/**
 * Render a design's card SVG for the given counts. `current` is shown verbatim
 * (may exceed `target`); `pct = round(current/target*100)` is clamped to
 * [0,100] and drives the fill geometry + the readout percentage.
 */
export function renderSubGoalDesignSvg(
  design: SubGoalDesign,
  counts: { current: number; target: number },
): string {
  const target = Math.max(1, Math.round(counts.target));
  const current = Math.max(0, Math.round(counts.current));
  const pct = clamp(Math.round((current / target) * 100), 0, 100);
  return renderDesign(design, { values: { current, target, pct } });
}

export function getSubGoalDesign(
  id: string | undefined,
): SubGoalDesign | undefined {
  if (!id) {
    return undefined;
  }
  const local = SUB_GOAL_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Fall back to an INSTALLED community design (hydrated into the registry by
  // useDesignLibrary). It's constrained to widgetType "subgoal", so the shape
  // matches SubGoalDesign at runtime; the cast only relaxes the `category`
  // literal. First-party ids always win above, so this only ADDS reach.
  return getInstalledDesign(id, "subgoal") as SubGoalDesign | undefined;
}
